import { useCallback, useEffect, useState } from 'react';

import { organizationService } from '@/services';
import type { Organization, OrganizationFilters } from '@/types';

interface UseOrganizationsResult {
  readonly organizations: readonly Organization[];
  readonly page: number;
  readonly pageSize: number;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => Promise<void>;
  readonly setPage: (page: number) => void;
  readonly setFilters: (filters: OrganizationFilters) => void;
}

const DEFAULT_FILTERS: OrganizationFilters = {
  min_confidence: 0,
  min_facility_count: 1,
  sort_by: 'facility_count',
  sort_order: 'desc',
};

export function useOrganizations(
  initialFilters?: OrganizationFilters,
  initialPageSize = 50
): UseOrganizationsResult {
  const [organizations, setOrganizations] = useState<readonly Organization[]>(
    []
  );
  const [page, setPage] = useState(1);
  const [pageSize] = useState(initialPageSize);
  const [filters, setFilters] = useState<OrganizationFilters>({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const offset = (page - 1) * pageSize;
      const data = await organizationService.fetchOrganizations({
        ...filters,
        limit: pageSize,
        offset,
      });
      setOrganizations(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch organizations';
      setError(message);
      console.error('Error fetching organizations:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleSetFilters = useCallback((newFilters: OrganizationFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPage(1); // Reset to first page when filters change
  }, []);

  return {
    organizations,
    page,
    pageSize,
    loading,
    error,
    refetch: fetchData,
    setPage,
    setFilters: handleSetFilters,
  };
}
